import { AUDIO_CONFIG } from "./config";
import { playProceduralChime } from "./chime";

/**
 * ProceduralAmbientEngine — a procedural, royalty-free ambient soundscape built
 * with the Web Audio API. It layers a soft tanpura-like drone, gentle wind,
 * distant flowing water and occasional singing-bowl resonance. No melodies, no
 * vocals, no rhythm — a calm sanctuary texture kept at a very low volume.
 *
 * Used as the DEFAULT placeholder. To swap in a produced soundscape, set
 * `ambientSrc` in audio/config.js — the FileAmbientEngine will be used instead.
 *
 * Interface (shared by every engine): fadeIn(seconds) / fadeOut(seconds) / playChime()
 */
export class ProceduralAmbientEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.chimeBus = null;
    this.started = false;
    this.bowlTimer = null;
    this.lfos = [];
  }

  _noiseBuffer(seconds = 4, brown = false) {
    const ctx = this.ctx;
    const length = ctx.sampleRate * seconds;
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < length; i += 1) {
      const white = Math.random() * 2 - 1;
      if (brown) {
        last = (last + 0.02 * white) / 1.02;
        data[i] = last * 3.2;
      } else {
        data[i] = white;
      }
    }
    return buffer;
  }

  _startLfo(param, freq, min, max) {
    const ctx = this.ctx;
    const lfo = ctx.createOscillator();
    const depth = ctx.createGain();
    lfo.frequency.value = freq;
    depth.gain.value = (max - min) / 2;
    param.value = (max + min) / 2;
    lfo.connect(depth).connect(param);
    lfo.start();
    this.lfos.push(lfo);
  }

  _build() {
    const ctx = this.ctx;

    this.master = ctx.createGain();
    this.master.gain.value = 0.0001;
    this.master.connect(ctx.destination);

    this.chimeBus = ctx.createGain();
    this.chimeBus.gain.value = 0.28;
    this.chimeBus.connect(ctx.destination);

    // ── Tanpura-like drone ──────────────────────────────
    const droneFilter = ctx.createBiquadFilter();
    droneFilter.type = "lowpass";
    droneFilter.frequency.value = 700;
    droneFilter.Q.value = 0.4;
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0.5;
    droneFilter.connect(droneGain).connect(this.master);

    const tonics = [65.41, 98.0, 130.81, 196.0]; // C2, G2, C3, G3 (tonic + fifth)
    tonics.forEach((freq, idx) => {
      [0, 0.4].forEach((detune) => {
        const osc = ctx.createOscillator();
        osc.type = idx > 1 ? "sine" : "triangle";
        osc.frequency.value = freq;
        osc.detune.value = detune * 100;
        const g = ctx.createGain();
        g.gain.value = 0.16 / (idx + 1);
        osc.connect(g).connect(droneFilter);
        osc.start();
      });
    });
    this._startLfo(droneGain.gain, 0.05, 0.32, 0.6);

    // ── Wind through trees (brown noise) ────────────────
    const wind = ctx.createBufferSource();
    wind.buffer = this._noiseBuffer(6, true);
    wind.loop = true;
    const windFilter = ctx.createBiquadFilter();
    windFilter.type = "lowpass";
    windFilter.Q.value = 0.6;
    const windGain = ctx.createGain();
    windGain.gain.value = 0.22;
    wind.connect(windFilter).connect(windGain).connect(this.master);
    wind.start();
    this._startLfo(windFilter.frequency, 0.06, 180, 620);
    this._startLfo(windGain.gain, 0.08, 0.12, 0.3);

    // ── Distant flowing water (filtered white noise) ────
    const water = ctx.createBufferSource();
    water.buffer = this._noiseBuffer(5, false);
    water.loop = true;
    const waterFilter = ctx.createBiquadFilter();
    waterFilter.type = "bandpass";
    waterFilter.frequency.value = 2200;
    waterFilter.Q.value = 0.7;
    const waterGain = ctx.createGain();
    waterGain.gain.value = 0.05;
    water.connect(waterFilter).connect(waterGain).connect(this.master);
    water.start();
    this._startLfo(waterFilter.frequency, 0.1, 1600, 3000);

    this.started = true;
  }

  _bowl(bus, { fundamental = 320, gain = 0.12, decay = 7 } = {}) {
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const partials = [1, 2.01, 2.74, 4.06];
    const bowlGain = ctx.createGain();
    bowlGain.gain.setValueAtTime(0.0001, now);
    bowlGain.gain.exponentialRampToValueAtTime(gain, now + 0.4);
    bowlGain.gain.exponentialRampToValueAtTime(0.0001, now + decay);
    bowlGain.connect(bus);
    partials.forEach((ratio, i) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = fundamental * ratio;
      const g = ctx.createGain();
      g.gain.value = 1 / (i + 1.6);
      osc.connect(g).connect(bowlGain);
      osc.start(now);
      osc.stop(now + decay + 0.2);
    });
  }

  _scheduleBowl() {
    clearTimeout(this.bowlTimer);
    const next = 18000 + Math.random() * 16000; // every 18–34s
    this.bowlTimer = setTimeout(() => {
      if (this.ctx && this.ctx.state === "running") {
        const roots = [261.63, 293.66, 329.63, 392.0];
        this._bowl(this.master, {
          fundamental: roots[Math.floor(Math.random() * roots.length)],
          gain: 0.1,
          decay: 8,
        });
        this._scheduleBowl();
      }
    }, next);
  }

  async fadeIn(time = AUDIO_CONFIG.fadeInSeconds) {
    if (!this.started) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this._build();
    }
    if (this.ctx.state === "suspended") {
      try {
        await this.ctx.resume();
      } catch (e) {
        /* ignore */
      }
    }
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(Math.max(this.master.gain.value, 0.0001), now);
    this.master.gain.linearRampToValueAtTime(AUDIO_CONFIG.volume, now + time);
    this._scheduleBowl();
    this._bowl(this.master, { fundamental: 261.63, gain: 0.09, decay: 7 });
  }

  fadeOut(time = AUDIO_CONFIG.fadeOutSeconds) {
    if (!this.started || !this.ctx) return;
    clearTimeout(this.bowlTimer);
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(0.0001, now + time);
    setTimeout(() => {
      if (this.ctx && this.ctx.state === "running") {
        this.ctx.suspend().catch(() => {});
      }
    }, time * 1000 + 120);
  }

  playChime() {
    if (AUDIO_CONFIG.chimeSrc) {
      const c = new Audio(AUDIO_CONFIG.chimeSrc);
      c.volume = Math.min(1, AUDIO_CONFIG.chimeVolume);
      c.play().catch(() => {});
      return;
    }
    if (!this.started || !this.ctx) {
      playProceduralChime(AUDIO_CONFIG.chimeVolume);
      return;
    }
    if (this.ctx.state === "suspended") this.ctx.resume().catch(() => {});
    this._bowl(this.chimeBus, { fundamental: 523.25, gain: 0.5, decay: 4.5 });
    setTimeout(() => {
      if (this.ctx && this.ctx.state === "running") {
        this._bowl(this.chimeBus, { fundamental: 783.99, gain: 0.28, decay: 3.5 });
      }
    }, 220);
  }
}
